import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Plus} from '../../icons';
import {SUGGESTION_BOARD, SUGGESTION_KINDS, suggestionKindOf} from '../rulesModel';
import {alertTitle, suggestionSentence} from '../ruleText';
import {PARAM_RULE, SCRATCH_PATH} from '../alertsCenterPaths';

/** El id del DOM de una sugerida (`?regla=` puede apuntar a una que todavía no es fila). */
export function suggestionAnchorId(systemKey) {
    return `alc-suggestion-${String(systemKey).replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

// Las que se ven antes de «Ver las N» (el mockup: seis tarjetas).
const FIRST_SHOWN = 6;
const KIND_ALL = '';

/**
 * «Crear una alerta» (`/alerts/new`, UD-18): primero las sugeridas, con el número que la agencia ya usa y un filtro por
 * tipo (Ritmo y gasto / Resultados / De tu Tablero); abajo, «Armar una desde cero», que abre el constructor.
 *
 * «Agregar» crea la fila al instante (`from-suggestion`, como antes de UD-18) y abre su editor para revisar el número y
 * a quién le llega (`onAdd`). `?regla=<clave>` (una sugerida de un correo o de la tuerca) la resalta y la trae a la
 * vista. La marca es `data-testid="alc-new"`.
 */
export function CreateAlertView({suggestions = [], canEdit = true, addingKey = null, onAdd}) {
    const {t, i18n} = useTranslation();
    const [searchParams] = useSearchParams();
    const highlightKey = searchParams.get(PARAM_RULE);
    const [kind, setKind] = useState(KIND_ALL);
    const [expanded, setExpanded] = useState(false);

    const counts = useMemo(() => {
        const result = {[KIND_ALL]: suggestions.length};
        SUGGESTION_KINDS.forEach((key) => {
            result[key] = suggestions.filter((item) => suggestionKindOf(item.system_key) === key).length;
        });
        return result;
    }, [suggestions]);
    const filtered = kind === KIND_ALL ? suggestions : suggestions.filter((item) => suggestionKindOf(item.system_key) === kind);

    // La sugerida del link tiene que estar a la vista: todas, sin filtro.
    const highlightIndex = highlightKey ? suggestions.findIndex((item) => item.system_key === highlightKey) : -1;
    const pendingScroll = useRef(null);
    useEffect(() => {
        if (highlightIndex < 0) return;
        setKind(KIND_ALL);
        if (highlightIndex >= FIRST_SHOWN) setExpanded(true);
        pendingScroll.current = highlightKey;
    }, [highlightIndex, highlightKey]);
    // El scroll espera a que la tarjeta esté dibujada: la séptima recién aparece en el render que abre el pliegue, y el
    // efecto de arriba no vuelve a correr (review C-ud18-fe-03). Corre después de cada render, pero sólo hace algo una vez.
    useEffect(() => {
        if (!pendingScroll.current) return;
        const card = document.getElementById(suggestionAnchorId(pendingScroll.current));
        if (!card) return;
        pendingScroll.current = null;
        if (card.scrollIntoView) card.scrollIntoView({block: 'center', behavior: 'smooth'});
    });

    const shown = expanded ? filtered : filtered.slice(0, FIRST_SHOWN);
    const hidden = filtered.length - shown.length;

    const renderCard = (suggestion) => {
        const title = alertTitle(suggestion, t);
        const cardKind = suggestionKindOf(suggestion.system_key);
        return (
            <li key={suggestion.system_key} id={suggestionAnchorId(suggestion.system_key)} data-system-key={suggestion.system_key}
                className={`alc-new-card${highlightKey === suggestion.system_key ? ' -highlight' : ''}`}>
                <span className={`alc-tag${cardKind === SUGGESTION_BOARD ? ' -board' : ''}`}>{t(`alerts_center.mr.kind_${cardKind}`)}</span>
                <h4 className="alc-new-card__title">{title}</h4>
                <p className="alc-new-card__text">{suggestionSentence(suggestion, {t, locale: i18n.language})}</p>
                {canEdit && (
                    <button type="button" className="alc-btn -ghost -sm alc-new-card__add" disabled={addingKey !== null}
                            aria-label={t('alerts_center.suggestions.add_label', {title, interpolation: {escapeValue: false}})}
                            onClick={() => onAdd(suggestion)}>
                        <Plus size={13} weight="bold" aria-hidden="true"/>
                        {addingKey === suggestion.system_key ? t('alerts_center.saving') : t('alerts_center.suggestions.add')}
                    </button>
                )}
            </li>
        );
    };

    return (
        <div className="alc-new" data-testid="alc-new">
            <h2 className="alc-ed-title">{t('alerts_center.mr.new_title')}</h2>
            <p className="alc-ed-sub">{t('alerts_center.mr.new_lede')}</p>

            {suggestions.length > 0 && (
                <section className="alc-new-sug" aria-labelledby="alc-new-sug-title" data-testid="alc-new-suggestions">
                    <header className="alc-new-sug__head">
                        <h3 id="alc-new-sug-title" className="alc-new-sug__title">
                            {t('alerts_center.mr.new_suggestions_title', {count: suggestions.length})}
                        </h3>
                        <div className="uiseg alc-filters" role="tablist" aria-label={t('alerts_center.mr.kind_label')}>
                            {[KIND_ALL, ...SUGGESTION_KINDS].map((key) => {
                                const active = key === kind;
                                return (
                                    <button key={key || 'all'} type="button" role="tab" aria-selected={active} data-kind={key || 'all'}
                                            className={`uiseg__btn${active ? ' -active' : ''}`}
                                            disabled={!active && counts[key] === 0}
                                            onClick={() => { setKind(key); setExpanded(false); }}>
                                        {t(key ? `alerts_center.mr.kind_${key}` : 'alerts_center.mr.kind_all')}
                                        <span className="uiseg__count">{counts[key]}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </header>
                    <ul className="alc-new-sug__grid">{shown.map(renderCard)}</ul>
                    {hidden > 0 && (
                        <div className="alc-new-sug__more">
                            <span>{t('alerts_center.mr.new_more', {count: hidden})}</span>
                            <button type="button" className="alc-btn -ghost -sm" onClick={() => setExpanded(true)}
                                    data-testid="alc-new-see-all">
                                {t('alerts_center.mr.new_see_all', {count: filtered.length})}
                            </button>
                        </div>
                    )}
                </section>
            )}

            <div className="alc-new-scratch" data-testid="alc-new-scratch">
                <div>
                    <strong>{t('alerts_center.mr.scratch_title')}</strong>
                    <p>{t('alerts_center.mr.scratch_text')}</p>
                </div>
                <Link to={SCRATCH_PATH} className="alc-btn -ghost">{t('alerts_center.mr.scratch_cta')}</Link>
            </div>
        </div>
    );
}

export default CreateAlertView;
